import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Typography from '../components/Text/Typography';
import Button from '../components/Button/Button';
import ContentfulImage from '../components/ContentfulImage/ContentfulImage';
import './ProductDetailPage.css';
import LoadingSpinner from "../components/LoadingSpinner";
import ResponsiveDialog from "../components/ResponsiveDialog/ResponsiveDialog";
import { API_BASE_URL, authenticatedFetch } from "../config/api";

const capitalizeWords = (str: string) => {
  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface Product {
  product_id: number;
  product_name: string;
  brand: string;
  category: string;
  type: string;
  skin_type?: string;
  texture?: string;
  highlights?: string[];
  suggested_usage?: string;
  ingredients?: string;
  image_url?: string;
}

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addingToCabinet, setAddingToCabinet] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE_URL}/api/products/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch product");
        return res.json();
      })
      .then((data) => {
        setProduct(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  const handleAddToCabinet = async (productId: number) => {
    if (!user || !product) return;
    
    setAddingToCabinet(true);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/user/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user?.id || user?.username,
          productId
        }),
      });

      if (!response.ok) throw new Error('Failed to add product to cabinet');
      
      // Close dialog and show success feedback
      setShowAddDialog(false);
      // You could add a success toast or message here
    } catch (error) {
      console.error('Error adding product to cabinet:', error);
      // You could add an error toast or message here
    } finally {
      setAddingToCabinet(false);
    }
  };

  const handleAddToCabinetClick = () => {
    if (!user) {
      // Prompt user to login
      alert('Please sign in to add products to your cabinet');
      return;
    }
    setShowAddDialog(true);
  };

  if (loading) {
    return <LoadingSpinner text="Loading product..." />;
  }
  if (error || !product) {
    return <Typography variant="body" style={{ color: 'var(--color-warning-red)' }}>{error || "Product not found."}</Typography>;
  }

  return (
    <div className="product-detail-page">
      <div className="product-header">
        <Typography variant="h1">{product.product_name}</Typography>
        <Typography variant="bodyS" style={{ color: 'var(--text-secondary)' }}>
          {product.brand}
        </Typography>
      </div>

      <div className="product-content">
        {product.image_url && (
          <div className="product-image">
            <ContentfulImage 
              src={product.image_url} 
              alt={product.product_name}
              width={400}
              height={400}
              fit="fill"
              quality={85}
              format="webp"
              responsive={true}
            />
          </div>
        )}

        <div className="product-info">
          <div className="info-section">
            <Typography variant="bodyS" style={{ color: 'var(--text-secondary)' }}>
              Category:
            </Typography>
            <Typography variant="bodyS">
              {capitalizeWords(product.category)}
            </Typography>
          </div>

          <div className="info-section">
            <Typography variant="bodyS" style={{ color: 'var(--text-secondary)' }}>
              Type:
            </Typography>
            <Typography variant="bodyS">
              {product.type}
            </Typography>
          </div>

          {product.texture && (
            <div className="info-section">
              <Typography variant="bodyS" style={{ color: 'var(--text-secondary)' }}>
                Texture:
              </Typography>
              <Typography variant="bodyS">
                {product.texture}
              </Typography>
            </div>
          )}

          {product.suggested_usage && (
            <div className="info-section">
              <Typography variant="bodyS" style={{ color: 'var(--text-secondary)' }}>
                How to use:
              </Typography>
              <Typography variant="bodyS">
                {product.suggested_usage.split('\n').map((step, index) => (
                  <div key={index} style={{ marginBottom: '4px' }}>
                    {index + 1}. {step}
                  </div>
                ))}
              </Typography>
            </div>
          )}

          {product.ingredients && (
            <div className="info-section">
              <Typography variant="bodyS" style={{ color: 'var(--text-secondary)' }}>
                Ingredients:
              </Typography>
              <Typography variant="bodyS">
                {product.ingredients}
              </Typography>
            </div>
          )}
        </div>
      </div>

      <div className="product-actions">
        <Button 
          variant="primary" 
          style={{ width: '100%' }}
          onClick={handleAddToCabinetClick}
          disabled={addingToCabinet}
        >
          {addingToCabinet ? 'Adding...' : 'Add to Cabinet'}
        </Button>
      </div>

      {/* Responsive Product Search Dialog/Bottom Sheet */}
      <ResponsiveDialog
        type="addProduct"
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAddProduct={handleAddToCabinet}
      />
    </div>
  );
};

export default ProductDetailPage; 